export type ParsedReplyPromptTemplate = {
  tone: string;
  considerations: string;
  mustInclude: string;
};

type ReplyPromptTemplateFields = {
  tone?: string | null;
  considerations?: string | null;
  mustInclude?: string | null;
  context?: string | null;
};

const CONTEXT_MARKERS = [
  {
    tonePrefix: /^Tonalität:\s*/i,
    consider: ' Zu berücksichtigen:',
    include: ' Enthalten:',
  },
  {
    tonePrefix: /^Tone:\s*/i,
    consider: ' Consider:',
    include: ' Include:',
  },
] as const;

/**
 * Prefer structured API fields; fall back to parsing legacy `context` blobs
 * (DE + EN marker formats). Never treat unresolved i18n key paths as copy.
 */
export function resolveReplyPromptTemplateFields(
  template: ReplyPromptTemplateFields | null | undefined,
): ParsedReplyPromptTemplate | null {
  if (!template) return null;

  const toneRaw = template.tone?.trim() ?? '';
  const considerationsRaw = template.considerations?.trim() ?? '';
  const mustIncludeRaw = template.mustInclude?.trim() ?? '';
  const structuredOk =
    (toneRaw || considerationsRaw || mustIncludeRaw) &&
    !looksLikeMissingI18nKey(toneRaw) &&
    !looksLikeMissingI18nKey(considerationsRaw) &&
    !looksLikeMissingI18nKey(mustIncludeRaw);

  if (structuredOk) {
    return {
      tone: toneRaw,
      considerations: considerationsRaw,
      mustInclude: mustIncludeRaw,
    };
  }

  const context = template.context?.trim() ?? '';
  if (!context || looksLikeMissingI18nKey(context)) return null;
  return parseReplyPromptTemplateContext(context);
}

function looksLikeMissingI18nKey(value: string): boolean {
  return value.startsWith('reply-prompt-templates.');
}

/** Parse freeform template `context` into the three editor fields. */
export function parseReplyPromptTemplateContext(
  context: string,
): ParsedReplyPromptTemplate {
  for (const markers of CONTEXT_MARKERS) {
    const iConsider = context.indexOf(markers.consider);
    const iInclude = context.indexOf(markers.include);
    if (iConsider === -1 || iInclude === -1 || iConsider >= iInclude) {
      continue;
    }
    return {
      tone: context
        .slice(0, iConsider)
        .replace(markers.tonePrefix, '')
        .replace(/\.\s*$/, '')
        .trim(),
      considerations: context
        .slice(iConsider + markers.consider.length, iInclude)
        .trim(),
      mustInclude: context.slice(iInclude + markers.include.length).trim(),
    };
  }

  return {
    tone: context
      .replace(/^Tonalität:\s*/i, '')
      .replace(/^Tone:\s*/i, '')
      .trim(),
    considerations: '',
    mustInclude: '',
  };
}
